import { Formik } from 'formik';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Drawer, Field, Form } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck } from '~/hooks';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';

export default function RevenueRecognitionEntryForm({ revenueRecognitionEntryId, projectId, onSaved, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();

  const [project, setProject] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projects(projectId).get();
        setProject(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId]);

  const [revenueRecognitionEntry, setRevenueRecognitionEntry] = useState(null);
  useEffect(() => {
    (async () => {
      if (!revenueRecognitionEntryId) {
        setRevenueRecognitionEntry({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .revenueRecognitionEntries(revenueRecognitionEntryId)
          .get();
        setRevenueRecognitionEntry(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, revenueRecognitionEntryId]);

  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);
    values = emptyStringToNull({ ...values });

    try {
      if (revenueRecognitionEntryId) {
        await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .revenueRecognitionEntries(revenueRecognitionEntryId)
          .patch(values);
      } else {
        await api.www.workspaces(workspace.id).projectSettings(projectId).revenueRecognitionEntries().post(values);
      }

      toast.success('Revenue Recognition Entry saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!project || !revenueRecognitionEntry) return null;

  const initialValues = mergeValues(
    {
      date: moment().format(dateFormats.isoDate),
      notes: '',
      amount: '',
    },
    revenueRecognitionEntry,
  );

  return (
    <Drawer
      title={revenueRecognitionEntryId ? 'Edit Revenue Recognition Entry' : 'Add Revenue Recognition Entry'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              date: Yup.date().label('Date').nullable().required(),
              notes: Yup.string().label('Notes').max(255),
              amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
            })}>
            {(formik) => {
              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.DayPicker name="date" placeholder="Date" clearable={false} />
                    </Form.Control>

                    <Form.Control>
                      <Field.Text name="notes" placeholder="Notes" maxLength={255} />
                    </Form.Control>
                  </Form.Section>

                  <Form.Section title="Billing">
                    <Field.Currency name="amount" placeholder="Amount" currency={project.currency} />
                  </Form.Section>

                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                        Save & Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
